import { motion } from 'framer-motion';
import { ADVERSE_COLOR, TIER_COLOR } from '@/types/profiler';
import type { AdverseCategory } from '@/types/profiler';
import { ConfidenceBar } from '@/components/profiler/ConfidenceBar';
import type { DossierSectionProps } from './types';

// ── helpers ───────────────────────────────────────────────────────────────────

function fmt(n: number | undefined, def = '—'): string {
  if (n === undefined || n === null) return def;
  return String(n);
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

function truncate(s: string, len: number): string {
  return s.length > len ? s.slice(0, len) + '…' : s;
}

// ── metric card ───────────────────────────────────────────────────────────────

interface MetricCardProps {
  label: string;
  value: string | number;
  color?: string;
  sub?: string;
}

function MetricCard({ label, value, color = '#00C8FF', sub }: MetricCardProps) {
  return (
    <div
      style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 8 }}
      className="flex flex-col gap-1 p-3"
    >
      <span style={{ color, fontFamily: 'monospace', fontWeight: 800, fontSize: 26, lineHeight: 1 }}>
        {value}
      </span>
      <span style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.08em' }}>
        {label}
      </span>
      {sub && (
        <span style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace' }}>{sub}</span>
      )}
    </div>
  );
}

// ── category dot ──────────────────────────────────────────────────────────────

function CategoryDot({ category }: { category: AdverseCategory }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 7,
        height: 7,
        borderRadius: '50%',
        background: ADVERSE_COLOR[category],
        flexShrink: 0,
        marginTop: 2,
      }}
    />
  );
}

// ── main component ────────────────────────────────────────────────────────────

export function Overview({ report, pipeline, risk }: DossierSectionProps) {
  const sanctions = pipeline?.sanctions;
  const adverse = pipeline?.adverse_media;
  const cluster = pipeline?.cluster ?? report.identifier_cluster;

  // derive counts
  const emailCount = cluster?.variants.filter(v => v.type === 'EMAIL').length ?? 0;
  const phoneCount = cluster?.variants.filter(v => v.type === 'PHONE').length ?? 0;
  const addressCount = cluster?.variants.filter(v => v.type === 'ADDRESS').length ?? 0;
  const socialCount = pipeline?.social?.accounts?.length ?? 0;
  const companyCount = pipeline?.corporate?.mca21?.companies_as_director?.length ?? 0;
  const cryptoReg = pipeline?.wealth?.crypto_registered;
  const sanctionsCount = sanctions?.hits?.length ?? 0;
  const adverseCount = adverse?.articles?.filter(a => a.category !== 'NOT_ADVERSE').length ?? 0;

  const topSanctions = (sanctions?.hits ?? []).slice(0, 3);
  const topAdverse = (adverse?.articles ?? []).filter(a => a.category !== 'NOT_ADVERSE').slice(0, 3);

  const metrics: MetricCardProps[] = [
    {
      label: 'SANCTIONS HITS',
      value: fmt(sanctionsCount),
      color: sanctionsCount > 0 ? '#FF1F1F' : '#00C853',
    },
    {
      label: 'ADVERSE ARTICLES',
      value: fmt(adverseCount),
      color: adverseCount > 3 ? '#FF6B1A' : adverseCount > 0 ? '#F0B429' : '#00C853',
    },
    { label: 'EMAIL ADDRESSES', value: fmt(emailCount), color: '#2979FF' },
    { label: 'PHONE NUMBERS', value: fmt(phoneCount), color: '#00C853' },
    { label: 'ADDRESSES', value: fmt(addressCount), color: '#F0B429' },
    { label: 'SOCIAL PLATFORMS', value: fmt(socialCount), color: '#00C8FF' },
    { label: 'COMPANIES', value: fmt(companyCount), color: '#F0B429' },
    {
      label: 'CRYPTO REGISTERED',
      value: cryptoReg === true ? 'YES' : cryptoReg === false ? 'NO' : '—',
      color: cryptoReg ? '#FF6B1A' : '#5A7A9A',
    },
  ];

  const tierColor = risk?.tier ? TIER_COLOR[risk.tier] : '#5A7A9A';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
      className="flex flex-col gap-5"
    >
      {/* 8-metric grid */}
      <div className="grid grid-cols-4 gap-3">
        {metrics.map(m => (
          <MetricCard key={m.label} {...m} />
        ))}
      </div>

      {/* FATF compliance statement */}
      <div
        style={{
          background: '#060E1A',
          border: '1px solid #1E2B3F',
          borderRadius: 8,
          padding: '12px 14px',
        }}
      >
        <p
          style={{
            color: '#5A7A9A',
            fontSize: 9,
            fontFamily: 'monospace',
            lineHeight: 1.6,
            letterSpacing: '0.04em',
          }}
        >
          COMPLIANCE STATEMENT — Screened in accordance with FATF Recommendation 10 (Customer Due Diligence),
          RBI KYC Master Direction 2016 (as amended), and PMLA Section 12 (Record-Keeping Obligations).
          Risk assessment is algorithmic and subject to analyst review prior to regulatory filing.
          Classification: TLP:AMBER — restricted to authorised compliance personnel.
        </p>
      </div>

      {/* Top sanctions hits */}
      {topSanctions.length > 0 && (
        <div>
          <p
            style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em', marginBottom: 10 }}
          >
            TOP SANCTIONS MATCHES
          </p>
          <div className="flex flex-col gap-2">
            {topSanctions.map((hit, i) => (
              <div
                key={hit.entity_id ?? i}
                style={{
                  background: '#0C1118',
                  border: '1px solid #FF1F1F30',
                  borderLeft: '3px solid #FF1F1F',
                  borderRadius: 8,
                  padding: '10px 12px',
                }}
              >
                <div className="flex items-center justify-between gap-3 mb-2">
                  <span style={{ color: '#DCE8F5', fontSize: 11, fontWeight: 600 }}>
                    {hit.caption}
                  </span>
                  <span
                    style={{
                      background: '#FF1F1F18',
                      color: '#FF1F1F',
                      border: '1px solid #FF1F1F30',
                      fontFamily: 'monospace',
                      fontSize: 9,
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: 99,
                      flexShrink: 0,
                    }}
                  >
                    {Math.round(hit.score * 100)}%
                  </span>
                </div>
                <div style={{ marginBottom: 6 }}>
                  <ConfidenceBar value={hit.score} color="#FF1F1F" height={3} />
                </div>
                <div className="flex flex-wrap gap-1">
                  {hit.datasets.map(ds => (
                    <span
                      key={ds}
                      style={{
                        background: '#FF1F1F10',
                        color: '#FF6B1A',
                        border: '1px solid #FF6B1A20',
                        fontSize: 8,
                        fontFamily: 'monospace',
                        padding: '1px 6px',
                        borderRadius: 99,
                      }}
                    >
                      {ds}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top adverse media */}
      {topAdverse.length > 0 && (
        <div>
          <p
            style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em', marginBottom: 10 }}
          >
            TOP ADVERSE MEDIA
          </p>
          <div className="flex flex-col gap-2">
            {topAdverse.map((article, i) => (
              <div
                key={i}
                style={{
                  background: '#0C1118',
                  border: '1px solid #182030',
                  borderLeft: `3px solid ${ADVERSE_COLOR[article.category]}`,
                  borderRadius: 8,
                  padding: '10px 12px',
                }}
              >
                <div className="flex items-start gap-2 mb-2">
                  <CategoryDot category={article.category} />
                  <span style={{ color: '#DCE8F5', fontSize: 11, fontWeight: 600, lineHeight: 1.4 }}>
                    {truncate(article.title, 80)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    <span style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace' }}>
                      {article.source}
                    </span>
                    <span style={{ color: '#283A50', fontSize: 9, fontFamily: 'monospace' }}>
                      {formatDate(article.published_date)}
                    </span>
                  </div>
                  <span
                    style={{
                      background: `${ADVERSE_COLOR[article.category]}15`,
                      color: ADVERSE_COLOR[article.category],
                      border: `1px solid ${ADVERSE_COLOR[article.category]}30`,
                      fontSize: 8,
                      fontFamily: 'monospace',
                      fontWeight: 700,
                      padding: '1px 6px',
                      borderRadius: 99,
                      flexShrink: 0,
                    }}
                  >
                    {article.category.replace('_', ' ')}
                  </span>
                </div>
                <ConfidenceBar value={article.confidence} color={ADVERSE_COLOR[article.category]} height={3} showLabel />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Score sub-grid */}
      {risk && (
        <div
          style={{ background: '#0C1118', border: `1px solid ${tierColor}20`, borderRadius: 8, padding: '12px 14px' }}
        >
          <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em', marginBottom: 10 }}>
            RISK SCORE BREAKDOWN
          </p>
          <div className="flex flex-col gap-2">
            {(
              [
                ['Sanctions', risk.sanctions_score, '#FF1F1F'],
                ['PEP / UBO', risk.pep_score, '#FF6B1A'],
                ['Adverse Media', risk.adverse_media_score, '#F0B429'],
                ['Enforcement', risk.enforcement_score, '#F0B429'],
                ['ATO / Breach', risk.ato_score, '#00C8FF'],
              ] as [string, number, string][]
            ).map(([lbl, val, col]) => (
              <div key={lbl} className="flex flex-col gap-1">
                <div className="flex justify-between">
                  <span style={{ color: '#5A7A9A', fontSize: 10, fontFamily: 'monospace' }}>{lbl}</span>
                  <span style={{ color: col, fontSize: 10, fontFamily: 'monospace', fontWeight: 700 }}>
                    {Math.round(val ?? 0)}
                  </span>
                </div>
                <ConfidenceBar value={(val ?? 0) / 100} color={col} height={3} />
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}
